// Modal and Flyout: the Helios dialog primitive on a native <dialog> opened with showModal(),
// so focus trapping, Esc, inert background and the top layer come from the browser.
import { I80Element, define, h, nextId } from "../base.js";
import { icon } from "../icons.js";

class DialogElement extends I80Element {
  static get observedAttributes() {
    return ["heading", "tagline", "icon", "size", "color", "description", "open", "dismiss-disabled", "dismiss-label"];
  }

  setup() {
    let dialog = this.part("dialog");
    if (!dialog) {
      const footer = [...this.children].filter((c) => c.getAttribute("slot") === "footer");
      const body = this.takeContent(footer);
      dialog = h("dialog", { "data-i80-part": "dialog" },
        h("div", { class: "i80-dialog-primitive__wrapper-header", "data-i80-part": "header" }),
        h("div", { class: "i80-dialog-primitive__wrapper-body" },
          h("div", { class: `i80-dialog-primitive__body ${this.prefix}__body`, tabindex: "0", "data-i80-part": "body" }, body)),
        h("div", { class: "i80-dialog-primitive__wrapper-footer" },
          footer.length ? h("div", { class: `i80-dialog-primitive__footer ${this.prefix}__footer`, "data-i80-part": "footer" }, footer) : null));
      this.append(dialog);
    }
    // A clone never keeps the original's open state or overlay.
    dialog.removeAttribute("open");
    this.querySelector(':scope > [data-i80-part="overlay"]')?.remove();
    this._dialog = dialog;
    this._body = dialog.querySelector('[data-i80-part="body"]');
    this._titleId = nextId(`${this.localName}-title`);
    dialog.setAttribute("aria-labelledby", this._titleId);

    // Esc: the browser fires "cancel"; route it through close() so dismiss-disabled and events apply.
    dialog.addEventListener("cancel", (e) => {
      e.preventDefault();
      this.close();
    });
    // <form method="dialog"> and dialog.close() end up here too.
    dialog.addEventListener("close", () => this._closed());
    dialog.addEventListener("click", (e) => {
      if (e.target.closest("[close]") && dialog.contains(e.target)) this.close();
    });
    this._outside = (e) => {
      if (this.closesOnOutsideClick && dialog.open && outside(dialog, e)) this.close();
    };
  }

  adopt() {
    for (const node of this.takeContent()) {
      if (node.nodeType === 1 && node.getAttribute("slot") === "footer") this._footerBox().append(node);
      else this._body.append(node);
    }
  }

  _footerBox() {
    const wrapper = this._dialog.querySelector(".i80-dialog-primitive__wrapper-footer");
    return wrapper.querySelector('[data-i80-part="footer"]')
      ?? wrapper.appendChild(h("div", { class: `i80-dialog-primitive__footer ${this.prefix}__footer`, "data-i80-part": "footer" }));
  }

  update() {
    const dialog = this._dialog;
    const p = this.prefix;
    dialog.className = ["i80-dialog-primitive__wrapper", p, ...this.modifiers()].join(" ");

    const heading = this.getAttribute("heading") ?? "";
    const tagline = this.getAttribute("tagline");
    const iconName = this.getAttribute("icon");
    const dismiss = h("button", { class: `i80-dismiss-button i80-dialog-primitive__dismiss ${p}__dismiss`, type: "button", "aria-label": this.getAttribute("dismiss-label") ?? "Dismiss" }, icon("x"));
    dismiss.addEventListener("click", () => this.close());
    const header = dialog.querySelector('[data-i80-part="header"]');
    const description = this.getAttribute("description");
    header.replaceChildren(...[
      h("div", { class: `i80-dialog-primitive__header ${p}__header` },
        iconName ? icon(iconName, { size: "24", className: `i80-dialog-primitive__icon ${p}__icon` }) : null,
        h("h1", { class: `i80-dialog-primitive__title ${p}__title`, id: this._titleId },
          tagline ? h("div", { class: `i80-text i80-typography-body-100 i80-dialog-primitive__tagline ${p}__tagline` }, tagline) : null,
          h("div", { class: "i80-text i80-typography-display-300 i80-font-weight-semibold" }, heading)),
        dismiss),
      description && this.hasDescription
        ? h("div", { class: `i80-dialog-primitive__description i80-typography-body-200 i80-foreground-primary ${p}__description` }, description)
        : null,
    ].filter(Boolean));

    const want = this.flag("open");
    if (want && !dialog.open && this.isConnected) this._open();
    else if (!want && dialog.open) dialog.close();
  }

  get closesOnOutsideClick() {
    return false;
  }

  get hasDescription() {
    return false;
  }

  show() {
    this.setAttribute("open", "");
  }

  close() {
    if (!this._dialog?.open) return;
    if (this.flag("dismiss-disabled")) return;
    if (!this.dispatchEvent(new CustomEvent("i80-close", { bubbles: true, cancelable: true }))) return;
    this._dialog.close();
  }

  get open() {
    return !!this._dialog?.open;
  }

  _open() {
    this._opener = document.activeElement;
    this._bodyOverflow = document.body.style.getPropertyValue("overflow");
    document.body.style.setProperty("overflow", "hidden");
    this._dialog.showModal();
    // data-i80-part so the late-children watcher treats it as ours, not as content.
    this._dialog.after(h("div", { class: `i80-dialog-primitive__overlay ${this.prefix}__overlay`, "data-i80-part": "overlay" }));
    document.addEventListener("pointerdown", this._outside, true);
    this.dispatchEvent(new CustomEvent("i80-open", { bubbles: true }));
  }

  _closed() {
    document.removeEventListener("pointerdown", this._outside, true);
    this.querySelector(':scope > [data-i80-part="overlay"]')?.remove();
    if (this._bodyOverflow) document.body.style.setProperty("overflow", this._bodyOverflow);
    else document.body.style.removeProperty("overflow");
    if (!document.body.getAttribute("style")) document.body.removeAttribute("style");
    if (this.hasAttribute("open")) this.removeAttribute("open");
    if (this._opener?.isConnected) this._opener.focus();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._dialog?.open) this._dialog.close();
  }
}

function outside(el, e) {
  const r = el.getBoundingClientRect();
  return e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
}

/**
 * <i80-modal id="delete-plan" heading="Delete plan?" color="critical" size="small">
 *   <p>This can't be undone.</p>
 *   <i80-button-set slot="footer">…</i80-button-set>
 * </i80-modal>
 * <i80-button opens="delete-plan">Delete</i80-button>
 *
 * Children with slot="footer" go in the footer, the rest in the body. Clicking any element
 * with a `close` attribute inside closes it, as does <form method="dialog">.
 */
export class I80Modal extends DialogElement {
  get prefix() {
    return "i80-modal";
  }

  modifiers() {
    return [
      `i80-modal--size-${this.choice("size", ["small", "medium", "large"], "medium")}`,
      `i80-modal--color-${this.choice("color", ["neutral", "warning", "critical"], "neutral")}`,
    ];
  }
}

/** Like <i80-modal>, sliding in from the side. Closes on a click outside; has a `description`. */
export class I80Flyout extends DialogElement {
  get prefix() {
    return "i80-flyout";
  }

  get closesOnOutsideClick() {
    return true;
  }

  get hasDescription() {
    return true;
  }

  modifiers() {
    return [`i80-flyout--size-${this.choice("size", ["medium", "large"], "medium")}`];
  }
}

// Any element with opens="<id>" opens the modal or flyout with that id.
document.addEventListener("click", (e) => {
  const trigger = e.target.closest?.("[opens]");
  if (!trigger) return;
  const target = document.getElementById(trigger.getAttribute("opens"));
  if (target && typeof target.show === "function") {
    e.preventDefault();
    target.show();
  }
});

define("i80-modal", I80Modal);
define("i80-flyout", I80Flyout);
